import { useState, useCallback, useRef } from 'react';
import type { Order, OrderItem, Payment, WalletTransaction, OrderStatus } from '../types';
import {
    fetchSingleOrderFromDb,
    fetchOrderItemsFromDb,
    fetchOrderPaymentsFromDb,
    fetchWalletTransactionsFromDb,
    deleteOrderItemFromDb,
    submitOrderItemEditToDb,
    fetchOrderStatusHistoryFromDb,
} from '../services/orderService';
import { fetchDeliveriesForOrderFromDb } from '../services/deliveryService';
import { supabase } from '../clients/supabaseClient';
import { getErrorMessage } from '../utils/errors';
import type { RealtimeChannel } from '@supabase/supabase-js';

export function useOrderDetail() {
    const [order,              setOrder]              = useState<Order | null>(null);
    const [items,              setItems]              = useState<OrderItem[]>([]);
    const [payments,           setPayments]           = useState<Payment[]>([]);
    const [walletTransactions, setWalletTransactions] = useState<WalletTransaction[]>([]);
    const [orderStatuses,      setOrderStatuses]      = useState<OrderStatus[] | null>(null);
    const [loading,            setLoading]            = useState(false);
    const [loadingStatuses,    setLoadingStatuses]    = useState(false);
    const [error,              setError]              = useState<string | null>(null);

    const realtimeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

    const fetchOrder = useCallback(async (orderId: string): Promise<Order | null> => {
        setLoading(true);
        setError(null);
        try {
            const [o, i, w] = await Promise.all([
                fetchSingleOrderFromDb(orderId),
                fetchOrderItemsFromDb(orderId),
                fetchWalletTransactionsFromDb(orderId),
            ]);
            setOrder(o ? { ...o, deliveries: [] } : null);
            setItems(i);
            setWalletTransactions(w);
            return o;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load order'));
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchPayments = useCallback(async (orderId: string): Promise<Payment[]> => {
        try {
            const data = await fetchOrderPaymentsFromDb(orderId);
            setPayments(data);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load payments'));
            return [];
        }
    }, []);

    const fetchDeliveries = useCallback(async (orderId: string) => {
        try {
            const data = await fetchDeliveriesForOrderFromDb(orderId);
            setOrder(prev => prev ? { ...prev, deliveries: data } : prev);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load deliveries'));
            return [];
        }
    }, []);

    const deleteItem = useCallback(async (
        orderId: string,
        itemId: string,
        reason: string
    ): Promise<void> => {
        await deleteOrderItemFromDb(orderId, itemId, reason);
        setItems(prev => prev.filter(i => i.id !== itemId));
    }, []);

    const editItem = useCallback(async (
        orderId: string,
        itemId: string,
        previousQty: number,
        newQty: number,
        reason: string
    ): Promise<void> => {
        await submitOrderItemEditToDb(orderId, itemId, previousQty, newQty, reason);
        setItems(prev => prev.map(i =>
            i.id === itemId
                ? { ...i, quantity: newQty, total_price: Number(i.unit_price) * newQty }
                : i
        ));
    }, []);

    const fetchStatusHistory = useCallback(async (orderId: string): Promise<OrderStatus[]> => {
        setLoadingStatuses(true);
        try {
            const data = await fetchOrderStatusHistoryFromDb(orderId);
            setOrderStatuses(data);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load status history'));
            return [];
        } finally {
            setLoadingStatuses(false);
        }
    }, []);

    const subscribeToOrderStatus = useCallback((orderId: string) => {
        if (realtimeChannelRef.current) {
            void supabase.removeChannel(realtimeChannelRef.current);
        }

        void fetchStatusHistory(orderId);

        interface OrderStatusHistoryRow {
            id: string;
            status_id: string;
            created_at: string;
            model_id: string | null;
            model_type: string | null;
            order_id: string;
        }

        const channel: RealtimeChannel = supabase
            .channel(`order-status-${orderId}`)
            .on<OrderStatusHistoryRow>(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'order_status_history',
                    filter: `order_id=eq.${orderId}`,
                },
                async (payload) => {
                    const newRow = payload.new;
                    const { data } = await supabase
                        .from('statuses')
                        .select('*')
                        .eq('id', newRow.status_id)
                        .single();

                    if (data) {
                        const entry: OrderStatus = {
                            id:         newRow.id,
                            status_id:  newRow.status_id,
                            created_at: newRow.created_at,
                            model_id:   newRow.model_id ?? undefined,
                            model_type: newRow.model_type ?? undefined,
                            status:     data,
                        };
                        setOrderStatuses(prev => prev ? [...prev, entry] : [entry]);
                    }
                }
            );

        channel.subscribe();
        realtimeChannelRef.current = channel;
    }, [fetchStatusHistory]);

    const unsubscribeFromOrderStatus = useCallback(() => {
        if (realtimeChannelRef.current) {
            void supabase.removeChannel(realtimeChannelRef.current);
            realtimeChannelRef.current = null;
        }
        setOrderStatuses(null);
    }, []);

    return {
        order, items, payments, walletTransactions,
        orderStatuses, loading, loadingStatuses, error,
        fetchOrder, fetchPayments, fetchDeliveries,
        deleteItem, editItem,
        fetchStatusHistory, subscribeToOrderStatus, unsubscribeFromOrderStatus,
    };
}